import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import ConsignmentStats from "@/pages/admin/consignaciones/components/ConsignmentStats";
import AdvisorAccountCards from "@/pages/admin/consignaciones/components/AdvisorAccountCards";
import DeliveryModal from "@/pages/admin/consignaciones/components/DeliveryModal";
import AdvisorPaymentModal from "@/pages/admin/consignaciones/components/AdvisorPaymentModal";
import MovementHistory from "@/pages/admin/consignaciones/components/MovementHistory";
import { buildAdvisorAccounts, type AdvisorAccount } from "@/utils/accounting";
import { exportAdvisorAccount, exportAllAdvisorAccounts } from "@/utils/advisorExport";
import { ghostButtonClass } from "@/components/base/fieldStyles";
import type { AdvisorDeliveryInput } from "@/store/AppStore";

const ConsignacionesPage = () => {
  const { data, deliverToAdvisor, addAdvisorMovement, deleteAdvisorMovement } = useApp();
  const accounts = useMemo(() => buildAdvisorAccounts(data), [data]);
  const [selectedId, setSelectedId] = useState("");
  const [deliverFor, setDeliverFor] = useState<AdvisorAccount | null>(null);
  const [payFor, setPayFor] = useState<AdvisorAccount | null>(null);
  const [feedback, setFeedback] = useState("");

  const selected = accounts.find((a) => a.advisor.id === selectedId) ?? accounts[0] ?? null;

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 2400);
  };

  const handleDeliver = async (input: AdvisorDeliveryInput) => {
    await deliverToAdvisor(input);
    setDeliverFor(null);
    notify("Movimiento de mercancía registrado.");
  };

  const handlePay = async (amount: number, note: string) => {
    if (!payFor) return;
    await addAdvisorMovement({
      id: "",
      advisorId: payFor.advisor.id,
      productId: "",
      productName: "",
      kind: "pago",
      qty: 0,
      unitValue: 0,
      amount,
      note,
      date: new Date().toISOString(),
    });
    setPayFor(null);
    notify("Abono registrado en la cuenta del asesor.");
  };

  const handleDelete = async (id: string) => {
    await deleteAdvisorMovement(id);
    notify("Movimiento eliminado.");
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Consignación & Ventas a Asesores"
        subtitle="Deja mercancía en consignación, véndele al asesor o registra sus abonos. Cada asesor muestra cuánto compró, cuánto tiene en consignación y cuánto debe."
      />

      {accounts.length === 0 ? (
        <div className="glass-panel rounded-lg px-5 py-12 text-center">
          <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
            <i className="ri-team-line text-2xl" />
          </span>
          <p className="text-sm text-foreground-700 font-semibold">Aún no hay asesores en la red</p>
          <p className="text-xs text-foreground-500 mt-1">
            Cuando se apruebe un asesor podrás dejarle mercancía y venderle desde aquí.
          </p>
        </div>
      ) : (
        <>
          <ConsignmentStats accounts={accounts} />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-5 mb-3">
            <div>
              <h3 className="editable-title text-base font-bold">Cuentas corrientes</h3>
              <p className="text-[11px] text-foreground-600">
                Compró · Consignado · Debe. Exporta la cuenta de cada asesor a Excel.
              </p>
            </div>
            <button
              onClick={() => void exportAllAdvisorAccounts(accounts)}
              className={ghostButtonClass}
            >
              <i className="ri-file-excel-2-line" /> Exportar consolidado
            </button>
          </div>

          <AdvisorAccountCards
            accounts={accounts}
            selectedId={selected?.advisor.id ?? ""}
            onSelect={setSelectedId}
            onDeliver={setDeliverFor}
            onPay={setPayFor}
            onExport={(a) => void exportAdvisorAccount(a)}
          />

          {feedback && (
            <div className="mt-4 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
              <i className="ri-checkbox-circle-line text-accent-300" />
              <p className="text-xs text-foreground-800">{feedback}</p>
            </div>
          )}

          {selected && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-history-line text-accent-300" />
                <h3 className="editable-title text-base font-bold">
                  Historial de {selected.advisor.names} {selected.advisor.surnames}
                </h3>
              </div>
              <MovementHistory movements={selected.movements} onDelete={handleDelete} />
            </div>
          )}

          <DeliveryModal
            open={deliverFor !== null}
            account={deliverFor}
            products={data.products}
            inventory={data.inventory}
            onClose={() => setDeliverFor(null)}
            onSubmit={handleDeliver}
          />
          <AdvisorPaymentModal
            open={payFor !== null}
            account={payFor}
            onClose={() => setPayFor(null)}
            onSubmit={handlePay}
          />
        </>
      )}
    </div>
  );
};

export default ConsignacionesPage;